import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

const PortDataContext = createContext();
const BACKEND_URL = (
  process.env.REACT_APP_THRUST_STAND_BACKEND_URL || "http://127.0.0.1:8000"
).replace(/\/$/, "");
const WEBSOCKET_URL = `${BACKEND_URL.replace(/^http/, "ws")}/ws`;
const MAX_LIVE_DATA_POINTS = 10000;

export const PortDataProvider = ({ children }) => {
  const [globalPortData, setGlobalPortData] = useState(null);
  const [liveData, setLiveData] = useState([]);
  const [selectedPort, setSelectedPort] = useState("");
  const [availablePorts, setAvailablePorts] = useState([]);
  const [serialStatus, setSerialStatus] = useState({ connected: false });
  const [isRunning, setIsRunning] = useState(false);
  const [connectionMessage, setConnectionMessage] = useState("");
  const socketRef = useRef(null);
  const serialStatusRef = useRef({ connected: false });

  const startThrust = useCallback(() => {
    if (!serialStatusRef.current.connected) {
      setConnectionMessage("Connect an Arduino before starting the thrust stand");
      return false;
    }
    if (socketRef.current) return true;

    setConnectionMessage("Starting live data stream...");
    const socket = new WebSocket(WEBSOCKET_URL);
    socketRef.current = socket;

    socket.onopen = () => {
      setIsRunning(true);
      setConnectionMessage("Thrust stand running - receiving live data");
    };
    socket.onmessage = (event) => {
      try {
        const packet = JSON.parse(event.data);
        if (packet.type === "data") {
          const dataPoint = {
            ...packet,
            time: packet.time || new Date().toISOString(),
          };
          setGlobalPortData(dataPoint);
          setLiveData((previous) => [
            ...previous,
            dataPoint,
          ].slice(-MAX_LIVE_DATA_POINTS));
        }
        if (packet.type === "status" && packet.message) {
          setConnectionMessage(packet.message);
        }
      } catch (error) {
        setConnectionMessage(`Invalid data from FastAPI: ${error.message}`);
      }
    };
    socket.onerror = () => {
      setConnectionMessage("Could not open the FastAPI WebSocket");
    };
    socket.onclose = () => {
      socketRef.current = null;
      setIsRunning(false);
    };
    return true;
  }, []);

  const request = useCallback(async (path, options = {}) => {
    const headers = { ...(options.headers || {}) };
    if (options.body) headers["Content-Type"] = "application/json";

    const response = await fetch(`${BACKEND_URL}${path}`, {
      ...options,
      headers,
    });
    const body = await response.json();
    if (!response.ok) {
      throw new Error(body.detail || body.error || `Request failed (${response.status})`);
    }
    return body;
  }, []);

  const refreshPorts = useCallback(async ({ silent = false } = {}) => {
    try {
      const result = await request("/api/ports");
      setAvailablePorts(result.ports || []);
      setSerialStatus(result.serial || { connected: false });
      serialStatusRef.current = result.serial || { connected: false };
      if (result.serial?.connected) startThrust();
      setSelectedPort((current) => {
        if (result.serial?.connected && result.serial.port) return result.serial.port;
        if (result.ports?.some((port) => port.device === current)) return current;
        return result.ports?.[0]?.device || "";
      });
      if (!silent) setConnectionMessage("");
    } catch (error) {
      if (!silent) {
        setConnectionMessage(`FastAPI service unavailable: ${error.message}`);
      }
    }
  }, [request, startThrust]);

  const connectSerial = useCallback(async (port, baudRate = 115200) => {
    try {
      const result = await request("/api/connect", {
        method: "POST",
        body: JSON.stringify({ port, baud_rate: baudRate }),
      });
      setSerialStatus(result.serial);
      serialStatusRef.current = result.serial;
      setSelectedPort(port);
      setConnectionMessage(`Connected to ${port}`);
      startThrust();
      return true;
    } catch (error) {
      setConnectionMessage(`Arduino connection failed: ${error.message}`);
      return false;
    }
  }, [request, startThrust]);

  const stopThrust = useCallback(() => {
    const socket = socketRef.current;
    if (!socket) return;
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ throttle: 0 }));
    }
    if (socket.readyState < WebSocket.CLOSING) {
      socket.close(1000, "Stopped by operator");
    }
    socketRef.current = null;
    setIsRunning(false);
    setConnectionMessage("Thrust stand stopped safely");
  }, []);

  const disconnectSerial = useCallback(async () => {
    stopThrust();
    try {
      const result = await request("/api/disconnect", { method: "POST" });
      setSerialStatus(result.serial);
      serialStatusRef.current = result.serial;
      setConnectionMessage("Arduino disconnected");
    } catch (error) {
      setConnectionMessage(`Disconnect failed: ${error.message}`);
    }
  }, [request, stopThrust]);

  const sendThrottle = useCallback((value) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    socket.send(JSON.stringify({ throttle: Math.max(0, Math.min(100, Number(value))) }));
    return true;
  }, []);

  useEffect(() => {
    refreshPorts();
    if (serialStatus.connected) return undefined;

    const refreshTimer = window.setInterval(() => {
      refreshPorts({ silent: true });
    }, 2000);
    return () => window.clearInterval(refreshTimer);
  }, [refreshPorts, serialStatus.connected]);

  useEffect(() => () => {
    const socket = socketRef.current;
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ throttle: 0 }));
      socket.close(1000, "Thrust stand page closed");
    }
  }, []);

  return (
    <PortDataContext.Provider value={{
      globalPortData,
      liveData,
      clearLiveData: () => setLiveData([]),
      selectedPort,
      setSelectedPort,
      availablePorts,
      serialStatus,
      isRunning,
      connectionMessage,
      refreshPorts,
      connectSerial,
      disconnectSerial,
      startThrust,
      stopThrust,
      sendThrottle,
    }}>
      {children}
    </PortDataContext.Provider>
  );
};

export const usePortData = () => useContext(PortDataContext);
